/**
 * Clarix — Project Dashboard  (updated for Milestone 11)
 *
 * M11 changes:
 *   UploadModal:
 *     - Loads supplier registry on open
 *     - Replaces free-text supplier field with searchable dropdown
 *     - "Register new supplier" opens inline quick-add form
 *     - Selected supplier_id sent to backend alongside supplier name
 *
 * All other behaviour unchanged from M10.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const V = {
  ink:    '#0c1830',
  ink2:   '#2a3956',
  ink3:   '#5a6680',
  ink4:   '#8a93a6',
  paper:  '#fbfaf6',
  paper2: '#f3f1ea',
  paper3: '#e6e2d5',
  line:   '#d8d3c2',
  gold:   '#c8a64b',
  good:   '#2a6f4f',
  goodSoft: '#e8f0eb',
  bad:    '#9c2b1a',
  badSoft: '#f4e8e5',
  warn:   '#a8731a',
  warnSoft: '#f5ecd9',
  serif:  "'Source Serif 4', Georgia, serif",
  sans:   "'Inter', -apple-system, system-ui, sans-serif",
  mono:   "'JetBrains Mono', ui-monospace, monospace",
};

// ─── FONT LOADER ──────────────────────────────────────────────────────────────

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

// ─── SHARED NAV ───────────────────────────────────────────────────────────────

function UtilBar({ projectName }) {
  return (
    <div style={s.utilBar}>
      <div style={s.utilLeft}>
        <div style={s.utilDot}></div>
      </div>
      <div style={s.tickerLabel}>LIVE INTEL</div>
      <div style={{ fontSize: 11, color: 'rgba(251,250,246,0.55)', fontFamily: V.sans, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        Currently viewing: {projectName}
      </div>
    </div>
  );
}

function TopNav({ onBack, projectName, user, logout }) {
  const navigate = useNavigate();
  const navItems = [
    { label: 'Negotiations', action: () => navigate('/') },
    { label: 'The Room',     action: null },
    { label: 'Suppliers',    action: () => navigate('/suppliers') },
    { label: 'Documents',    action: null },
    { label: 'Reports',      action: null },
  ];

  return (
    <div style={s.nav}>
      <div style={s.wordmark} onClick={() => navigate('/')} role="button">
        <span style={s.wordmarkV}>V</span>
        <span style={s.wordmarkRest}>erdict</span>
      </div>
      <div style={s.wordmarkDivider}></div>
      <div style={s.wordmarkTag}>Negotiation Intelligence</div>
      <div style={s.navItems}>
        {navItems.map(item => (
          <div
            key={item.label}
            style={{
              ...s.navItem,
              color: item.label === 'Negotiations' ? V.ink : V.ink3,
              borderBottom: item.label === 'Negotiations' ? `2px solid ${V.gold}` : '2px solid transparent',
            }}
            onClick={() => item.action && item.action()}
          >
            {item.label}
          </div>
        ))}
      </div>
      <div style={s.navRight}>
        {user && <span style={s.navUser}>{user.email}</span>}
        {logout && <button style={s.navSignOut} onClick={logout}>Sign out</button>}
      </div>
    </div>
  );
}

function SubTabs({ active, onChange, projectId, navigate }) {
  const tabs = ['1 · Dashboard', '2 · The Negotiation Room', '3 · Evidence Locker'];
  const actions = [
    null,
    (docIds) => {
      // The Room needs at least 2 docs selected — navigate to documents to select them
      navigate(`/projects/${projectId}/documents`);
    },
    () => navigate(`/projects/${projectId}/documents`),
  ];

  return (
    <div style={s.subTabBar}>
      <span style={s.subTabView}>VIEW</span>
      {tabs.map((tab, i) => (
        <button
          key={tab}
          style={{
            ...s.subTab,
            background: active === i ? V.ink : 'transparent',
            color: active === i ? V.paper : V.ink3,
            border: `1px solid ${active === i ? V.ink : V.line}`,
          }}
          onClick={() => actions[i] ? actions[i]() : onChange(i)}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function formatValue(v, cur = 'EUR') {
  if (!v) return '—';
  const sym = cur === 'USD' ? '$' : cur === 'GBP' ? '£' : '€';
  return `${sym}${Number(v).toLocaleString('en-GB')}`;
}

function TypeTag({ type }) {
  const cfg = {
    'Contract':        { color: V.good,   bg: V.goodSoft },
    'Amendment':       { color: V.bad,    bg: V.badSoft  },
    'Proposal':        { color: V.warn,   bg: V.warnSoft },
    'Quotation':       { color: '#1a4d8f', bg: '#dbeafe' },
    'BOQ':             { color: '#1a4d8f', bg: '#dbeafe' },
    'Variation Order': { color: '#5b21b6', bg: '#ede9fe' },
    'Purchase Order':  { color: V.warn,   bg: V.warnSoft },
    'Invoice':         { color: V.ink2,   bg: V.paper3   },
  }[type] || { color: V.ink3, bg: V.paper2 };

  return (
    <span style={{
      fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 1,
      padding: '2px 7px', borderRadius: 3, textTransform: 'uppercase',
      color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap',
    }}>
      {type || '—'}
    </span>
  );
}

// ─── SUPPLIER DROPDOWN ────────────────────────────────────────────────────────
// A searchable dropdown that fetches from the supplier registry.
// Shows "Register new supplier" option to add inline.

const EMPTY_SUP_FORM = { name: '', category: 'other', country: '', contact_name: '', contact_email: '' };

const SUP_CATEGORIES = [
  { value: 'steel',      label: 'Steel'      },
  { value: 'bunkers',    label: 'Bunkers'    },
  { value: 'electrical', label: 'Electrical' },
  { value: 'civil',      label: 'Civil'      },
  { value: 'equipment',  label: 'Equipment'  },
  { value: 'services',   label: 'Services'   },
  { value: 'other',      label: 'Other'      },
];

function SupplierDropdown({ value, onChange, onNew }) {
  // value = { id, name } | null
  const [search, setSearch]     = useState('');
  const [options, setOptions]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const ref                     = useRef(null);

  // Load suppliers when search changes (debounced)
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      api.get(`/api/suppliers?is_active=1${search ? `&search=${encodeURIComponent(search)}` : ''}`)
        .then(res => setOptions(res.data.suppliers || []))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayText = value ? value.name : '';

  const handleSelect = (sup) => {
    onChange({ id: sup.id, name: sup.name });
    setSearch('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearch('');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        style={{
          ...s.input,
          display: 'flex', alignItems: 'center', cursor: 'pointer',
          background: open ? '#fdf8ee' : V.paper,
          borderColor: open ? V.gold : V.line,
        }}
        onClick={() => { setOpen(o => !o); }}
      >
        {value ? (
          <>
            <span style={{ flex: 1, fontSize: 13, color: V.ink, fontFamily: V.sans }}>{value.name}</span>
            <span
              style={{ fontSize: 14, color: V.ink4, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
              onClick={handleClear}
              title="Clear supplier"
            >
              ×
            </span>
          </>
        ) : (
          <>
            {open
              ? <input
                  autoFocus
                  style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, color: V.ink, fontFamily: V.sans, outline: 'none', padding: 0 }}
                  placeholder="Type to search suppliers…"
                  value={search}
                  onChange={e => { e.stopPropagation(); setSearch(e.target.value); }}
                  onClick={e => e.stopPropagation()}
                />
              : <span style={{ flex: 1, fontSize: 13, color: V.ink4, fontFamily: V.sans }}>Select or search supplier…</span>
            }
            <span style={{ fontSize: 11, color: V.ink4 }}>▾</span>
          </>
        )}
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: V.paper, border: `1px solid ${V.gold}`, borderTop: 'none',
          borderRadius: '0 0 4px 4px', boxShadow: '0 8px 24px rgba(12,24,48,0.12)',
          maxHeight: 200, overflowY: 'auto',
        }}>
          {loading && (
            <div style={{ padding: '10px 14px', fontSize: 12, color: V.ink4, fontFamily: V.sans }}>Loading…</div>
          )}
          {!loading && options.length === 0 && (
            <div style={{ padding: '10px 14px', fontSize: 12, color: V.ink4, fontFamily: V.sans }}>
              {search ? `No supplier found for "${search}"` : 'No suppliers in registry yet'}
            </div>
          )}
          {!loading && options.map(sup => (
            <div
              key={sup.id}
              style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: V.ink, fontFamily: V.sans, borderBottom: `0.5px solid ${V.line}` }}
              onMouseEnter={e => e.currentTarget.style.background = V.paper2}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => handleSelect(sup)}
            >
              <div style={{ fontWeight: 500 }}>{sup.name}</div>
              <div style={{ fontSize: 10, color: V.ink4, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: V.mono }}>{sup.category} · {sup.country || 'No country'}</div>
            </div>
          ))}
          {/* Register new option */}
          <div
            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 12, color: V.gold, fontFamily: V.sans, fontWeight: 600, borderTop: `1px solid ${V.line}`, display: 'flex', alignItems: 'center', gap: 6 }}
            onMouseEnter={e => e.currentTarget.style.background = '#fdf8ee'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => { setOpen(false); onNew(search); }}
          >
            + Register new supplier{search ? ` "${search}"` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── QUICK REGISTER SUPPLIER PANEL ───────────────────────────────────────────
// Appears inline inside the upload modal when user clicks "+ Register new supplier"

function QuickRegisterSupplier({ initialName, onCreated, onCancel }) {
  const [form, setForm]     = useState({ name: initialName || '', category: 'other', country: '', contact_name: '', contact_email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.name.trim()) { setError('Supplier name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const res = await api.post('/api/suppliers', form);
      onCreated({ id: res.data.supplier.id, name: res.data.supplier.name });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create supplier.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background: '#fdf8ee', border: `1px solid ${V.gold}`, borderRadius: 6, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink3, fontFamily: V.sans }}>Register New Supplier</div>
        <button style={{ background: 'none', border: 'none', color: V.ink4, fontSize: 14, cursor: 'pointer' }} onClick={onCancel}>✕</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 10 }}>
        <div style={s.field}>
          <label style={s.fieldLabel}>Company name <span style={{ color: V.gold }}>*</span></label>
          <input style={s.input} placeholder="Supplier name" value={form.name} onChange={e => setF('name', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.fieldLabel}>Category</label>
          <select style={s.input} value={form.category} onChange={e => setF('category', e.target.value)}>
            {SUP_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={s.field}>
          <label style={s.fieldLabel}>Country</label>
          <input style={s.input} placeholder="Greece" value={form.country} onChange={e => setF('country', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.fieldLabel}>Contact email</label>
          <input style={s.input} type="email" placeholder="contact@company.com" value={form.contact_email} onChange={e => setF('contact_email', e.target.value)} />
        </div>
      </div>
      {error && <div style={s.errorBox}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={s.btnGhost} onClick={onCancel} disabled={saving}>Cancel</button>
        <button style={{ ...s.btnGold, opacity: saving ? 0.6 : 1, padding: '7px 16px', fontSize: 12 }} onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating…' : 'Create & select'}
        </button>
      </div>
    </div>
  );
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────

function UploadModal({ projectId, onClose, onSuccess }) {
  const [file, setFile]       = useState(null);
  const [form, setForm]       = useState({
    document_type: 'Quotation', document_date: '',
    total_value: '', currency: 'EUR', notes: '',
  });
  const [supplier, setSupplier]       = useState(null);    // { id, name } | null
  const [showQuickReg, setShowQuickReg] = useState(false);
  const [quickRegName, setQuickRegName] = useState('');
  const [uploading, setUploading]     = useState(false);
  const [error, setError]             = useState(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleUpload = async () => {
    if (!file) { setError('Please select a file.'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', projectId);
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (supplier) {
        fd.append('supplier_id', supplier.id);
        fd.append('supplier',    supplier.name);
      }
      await api.post(`/api/documents/upload/${projectId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally { setUploading(false); }
  };

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        <div style={{ ...s.modalHeader, borderTop: `3px solid ${V.gold}` }}>
          <div>
            <div style={s.modalTitle}>Upload document</div>
            <div style={s.modalSub}>Add a quotation, contract, amendment or other document</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>

          {/* ── File picker ──────────────────────────────────────────────── */}
          <div style={s.field}>
            <label style={s.fieldLabel}>File <span style={{ color: V.gold }}>*</span></label>
            <div
              style={{ ...s.dropZone, borderColor: file ? V.gold : V.line, background: file ? '#fdf8ee' : V.paper }}
              onClick={() => document.getElementById('verdict-file-input').click()}
            >
              <input
                id="verdict-file-input"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg"
                style={{ display: 'none' }}
                onChange={e => setFile(e.target.files[0])}
              />
              {file ? (
                <div style={{ fontSize: 13, color: V.ink2, fontFamily: V.sans }}>
                  <strong>{file.name}</strong>
                  <span style={{ color: V.ink4, marginLeft: 8 }}>({(file.size / 1024).toFixed(0)} KB)</span>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: V.ink4, fontFamily: V.sans }}>
                  Click to select file — PDF, Word, Excel
                </div>
              )}
            </div>
          </div>

          {/* ── Supplier (registry dropdown) + Document type ─────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={s.field}>
              <label style={s.fieldLabel}>Supplier <span style={{ color: V.ink4, fontWeight: 400 }}>(optional)</span></label>
              {!showQuickReg ? (
                <SupplierDropdown
                  value={supplier}
                  onChange={setSupplier}
                  onNew={(initialName) => { setQuickRegName(initialName); setShowQuickReg(true); }}
                />
              ) : (
                <div style={{ fontSize: 12, color: V.ink3, fontFamily: V.sans, padding: '10px 0' }}>
                  Fill in the form below ↓
                </div>
              )}
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Document type</label>
              <select style={s.input} value={form.document_type} onChange={e => setF('document_type', e.target.value)}>
                {['Quotation', 'Proposal', 'Contract', 'Amendment', 'BOQ', 'Purchase Order', 'Variation Order', 'Invoice'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Quick register panel (inline) ─────────────────────────────── */}
          {showQuickReg && (
            <QuickRegisterSupplier
              initialName={quickRegName}
              onCreated={(newSup) => {
                setSupplier(newSup);
                setShowQuickReg(false);
                setQuickRegName('');
              }}
              onCancel={() => { setShowQuickReg(false); setQuickRegName(''); }}
            />
          )}

          {/* ── Date + Value ─────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={s.field}>
              <label style={s.fieldLabel}>Document date</label>
              <input style={s.input} type="date" value={form.document_date} onChange={e => setF('document_date', e.target.value)} />
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Total value</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select style={{ ...s.input, width: 80, flexShrink: 0 }} value={form.currency} onChange={e => setF('currency', e.target.value)}>
                  {['EUR', 'USD', 'GBP', 'AED'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input style={s.input} type="number" placeholder="0.00" value={form.total_value} onChange={e => setF('total_value', e.target.value)} />
              </div>
            </div>
          </div>

          {/* ── Notes ────────────────────────────────────────────────────── */}
          <div style={s.field}>
            <label style={s.fieldLabel}>Notes <span style={{ color: V.ink4, fontWeight: 400 }}>(optional)</span></label>
            <textarea style={{ ...s.input, resize: 'vertical', minHeight: 60 }} placeholder="Any context or observations…" value={form.notes} onChange={e => setF('notes', e.target.value)} />
          </div>

          {/* ── Registry info pill ───────────────────────────────────────── */}
          {supplier && !showQuickReg && (
            <div style={{ background: V.goodSoft, border: `1px solid #b8d8c4`, borderRadius: 4, padding: '8px 12px', fontSize: 11, color: V.good, fontFamily: V.sans, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>✓</span>
              This document will be linked to <strong>{supplier.name}</strong> in the registry and marked for data extraction.
            </div>
          )}

          {error && (
            <div style={s.errorBox}>{error}</div>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnGhost} onClick={onClose} disabled={uploading}>Cancel</button>
          <button style={{ ...s.btnGold, opacity: uploading ? 0.6 : 1 }} onClick={handleUpload} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload document'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FILE PREVIEW PANEL ───────────────────────────────────────────────────────

function PreviewPanel({ doc, onClose }) {
  const token   = localStorage.getItem('clarix_token');
  const API     = process.env.REACT_APP_API_URL || '';
  const fileUrl     = doc ? `${API}/api/documents/${doc.id}/file?token=${token}` : null;
  const downloadUrl = doc ? `${API}/api/documents/${doc.id}/download?token=${token}` : null;
  const _name    = doc?.original_name?.toLowerCase() || '';
  const isPdf   = _name.endsWith('.pdf') || _name.endsWith('.docx') || _name.endsWith('.doc') || _name.endsWith('.xlsm') || _name.endsWith('.xlsx') || _name.endsWith('.xls');
  const isOffice = isPdf && !_name.endsWith('.pdf');
  const [iframeLoaded, setIframeLoaded] = React.useState(false);
  React.useEffect(() => { setIframeLoaded(false); }, [doc?.id]);

  return (
    <div style={s.previewOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.previewPanel}>
        <div style={s.previewHeader}>
          <div>
            <div style={{ fontFamily: V.mono, fontSize: 11, color: V.ink3, marginBottom: 3 }}>{doc?.document_type}</div>
            <div style={{ fontFamily: V.serif, fontSize: 16, fontWeight: 500, color: V.ink }}>{doc?.original_name}</div>
            <div style={{ fontSize: 11, color: V.ink4, marginTop: 2, fontFamily: V.sans }}>{doc?.supplier || '—'} · {doc?.document_date || 'No date'}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <a href={downloadUrl} style={s.btnGhost}>↓ Download</a>
            <button style={s.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>
        <div style={s.previewBody}>
          {isPdf ? (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              {!iframeLoaded && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#f3f1ea', zIndex: 2 }}>
                  <div style={{ fontSize: 28, color: '#c8a64b', animation: 'spin 1.2s linear infinite' }}>↻</div>
                  <div style={{ fontSize: 13, color: '#5a6680', fontFamily: 'Inter, sans-serif' }}>
                    {isOffice ? 'Converting document for preview…' : 'Loading preview…'}
                  </div>
                  {isOffice && <div style={{ fontSize: 11, color: '#8a93a6', fontFamily: 'Inter, sans-serif' }}>This may take a few seconds</div>}
                </div>
              )}
              <iframe
                src={fileUrl}
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: 4, opacity: iframeLoaded ? 1 : 0 }}
                title={doc?.original_name}
                onLoad={() => setIframeLoaded(true)}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
              <div style={{ fontSize: 32, color: V.line }}>◻</div>
              <div style={{ fontSize: 13, color: V.ink4, fontFamily: V.sans }}>Preview not available for this file type</div>
              <a href={fileUrl} download style={s.btnGold}>Download to view</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function ProjectDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [project, setProject]     = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploadOpen, setUploadOpen]     = useState(false);
  const [previewDoc, setPreviewDoc]     = useState(null);
  const [activeTab, setActiveTab]       = useState(0);
  const [statusOpen, setStatusOpen]     = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    setStatusOpen(false);
    try {
      await api.put(`/api/projects/${id}`, { status: newStatus });
      load();
    } catch (e) {
      console.error('Status update failed:', e);
    } finally { setUpdatingStatus(false); }
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/api/projects/${id}`),
      api.get(`/api/documents?project_id=${id}`),
    ])
      .then(([projRes, docsRes]) => {
        setProject(projRes.data.project);
        setDocuments(docsRes.data.documents || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: V.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: V.sans, color: V.ink4 }}>
      Loading negotiation…
    </div>
  );

  if (!project) return (
    <div style={{ minHeight: '100vh', background: V.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: V.sans, color: V.ink4 }}>
      Negotiation not found
    </div>
  );

  const awarded      = documents.filter(d => d.awarded);
  const awardedValue = awarded.reduce((sum, d) => sum + (d.total_value || 0), 0);
  const suppliers    = [...new Set(documents.map(d => d.supplier).filter(Boolean))];
  const quotations   = documents.filter(d => d.document_type === 'Quotation' || d.document_type === 'Proposal');
  const amendments   = documents.filter(d => d.document_type === 'Amendment');

  const indCfg = {
    maritime:     { label: 'Maritime',     icon: '⚓', color: '#1a4d8f', bg: '#dbeafe' },
    construction: { label: 'Construction', icon: '🏗', color: '#7a4a00', bg: '#fef3c7' },
    generic:      { label: 'General',      icon: '◈', color: V.ink2,   bg: V.paper2  },
  }[project.industry] || { label: 'General', icon: '◈', color: V.ink2, bg: V.paper2 };

  const statusCfg = {
    active:  { label: 'Active',   color: V.good, bg: V.goodSoft },
    on_hold: { label: 'On Hold',  color: V.warn, bg: V.warnSoft },
    closed:  { label: 'Closed',   color: V.ink4, bg: V.paper2   },
  }[project.status] || { label: project.status, color: V.ink4, bg: V.paper2 };

  return (
    <div style={s.wrap}>
      <FontLoader />

      <UtilBar projectName={project.name} />
      <TopNav user={user} logout={logout} onBack={() => navigate('/')} projectName={project.name} />
      <SubTabs active={activeTab} onChange={setActiveTab} projectId={id} navigate={navigate} />

      {/* Breadcrumb bar */}
      <div style={s.breadBar}>
        <span style={s.breadLink} onClick={() => navigate('/')}>Negotiations</span>
        <span style={s.breadSep}>/</span>
        <span style={s.breadCurrent}>{project.name}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          <button style={s.btnGhost} onClick={() => navigate(`/projects/${id}/documents`)}>
            Documents
          </button>
          <button style={s.btnGhost} onClick={() => navigate(`/projects/${id}/chat`)}>
            Consigliere
          </button>
          <button style={s.btnGhost} onClick={() => navigate(`/projects/${id}/timeline`)}>
            Timeline
          </button>
          <button style={s.btnGold} onClick={() => setUploadOpen(true)}>
            ↑ Upload document
          </button>
        </div>
      </div>

      <div style={s.body}>

        {/* Page heading */}
        <div style={s.pageHead}>
          <div>
            <div style={s.pageEyebrow}>
              <span style={{ ...s.indBadge, color: indCfg.color, background: indCfg.bg }}>
                {indCfg.icon} {indCfg.label.toUpperCase()}
              </span>
              <span style={s.eyebrowSep}>·</span>
              <span>{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
              <span style={s.eyebrowSep}>·</span>
              <span>{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''} seated</span>
            </div>
            <div style={s.pageTitle}>{project.name}</div>
            {project.notes && <div style={s.pageNotes}>{project.notes}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {/* Clickable status badge */}
            <div style={{ position: 'relative' }}>
              <span
                style={{ ...s.statusBadge, color: statusCfg.color, background: statusCfg.bg, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => setStatusOpen(o => !o)}
                title="Click to change status"
              >
                {updatingStatus ? '…' : statusCfg.label}
                <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
              </span>
              {statusOpen && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: V.paper, border: `1px solid ${V.line}`, borderRadius: 4, boxShadow: '0 8px 24px rgba(12,24,48,0.12)', zIndex: 50, minWidth: 130, overflow: 'hidden' }}>
                  {[
                    { value: 'active',  label: 'Active',   color: V.good,  bg: '#e8f0eb' },
                    { value: 'on_hold', label: 'On Hold',  color: V.warn,  bg: '#f5ecd9' },
                    { value: 'closed',  label: 'Closed',   color: V.ink4,  bg: V.paper2  },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => handleStatusChange(opt.value)}
                      style={{ padding: '10px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: opt.color, background: project.status === opt.value ? opt.bg : 'transparent', fontFamily: V.sans }}
                      onMouseEnter={e => e.currentTarget.style.background = opt.bg}
                      onMouseLeave={e => e.currentTarget.style.background = project.status === opt.value ? opt.bg : 'transparent'}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {quotations.length >= 2 && (
              <button style={s.btnPrimary} onClick={() => navigate(`/projects/${id}/documents`)}>
                Enter the room →
              </button>
            )}
          </div>
        </div>

        {/* KPI strip */}
        <div style={s.kpiStrip}>
          {[
            {
              label: 'DOCUMENTS',
              value: documents.length,
              sub: documents.length === 0 ? 'None uploaded yet' : `${quotations.length} quotation${quotations.length !== 1 ? 's' : ''} · ${amendments.length} amendment${amendments.length !== 1 ? 's' : ''}`,
            },
            {
              label: 'SUPPLIERS SEATED',
              value: suppliers.length,
              sub: suppliers.slice(0, 2).join(' · ') || 'None yet',
            },
            {
              label: 'CONTRACT VALUE',
              value: awardedValue > 0 ? formatValue(awardedValue) : '—',
              sub: awarded.length > 0 ? `${awarded.length} awarded` : 'None awarded yet',
              accent: awardedValue > 0,
            },
            {
              label: 'STATUS',
              value: statusCfg.label,
              sub: project.project_code || 'No project code',
            },
          ].map((k, i) => (
            <div key={i} style={{ ...s.kpi, borderTop: k.accent ? `2px solid ${V.gold}` : `2px solid ${V.line}` }}>
              <div style={s.kpiLabel}>{k.label}</div>
              <div style={{ ...s.kpiVal, color: k.accent ? V.gold : V.ink, fontSize: typeof k.value === 'string' && k.value.length > 8 ? 22 : 32 }}>{k.value}</div>
              <div style={s.kpiSub}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Two-col layout */}
        <div style={s.twoCol}>

          {/* Left — documents */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <div style={s.cardTitle}>Evidence on the table</div>
              <span style={s.cardLink} onClick={() => navigate(`/projects/${id}/documents`)}>
                View all →
              </span>
            </div>

            {documents.length === 0 ? (
              <div style={s.emptyState}>
                <div style={{ fontSize: 13, color: V.ink4, marginBottom: 12 }}>No documents yet — upload the first one to begin analysis.</div>
                <button style={s.btnGold} onClick={() => setUploadOpen(true)}>Upload first document</button>
              </div>
            ) : (
              <div style={s.docList}>
                {documents.slice(0, 8).map(doc => (
                  <div
                    key={doc.id}
                    style={s.docRow}
                    onMouseEnter={e => e.currentTarget.style.background = V.paper2}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={s.docInfo} onClick={() => setPreviewDoc(doc)}>
                      <div style={s.docName}>{doc.original_name}</div>
                      <div style={s.docMeta}>{doc.supplier_name || doc.supplier || 'No supplier'} · {doc.document_date || 'No date'}</div>
                    </div>
                    <div style={s.docRight}>
                      <TypeTag type={doc.document_type} />
                      <div style={s.docVal}>{formatValue(doc.total_value, doc.currency)}</div>
                      <button
                        style={s.previewBtn}
                        onClick={() => setPreviewDoc(doc)}
                        title="Preview file"
                      >
                        ⊙
                      </button>
                    </div>
                  </div>
                ))}
                {documents.length > 8 && (
                  <div style={{ fontSize: 12, color: V.ink4, padding: '8px 10px', fontFamily: V.sans, cursor: 'pointer' }} onClick={() => navigate(`/projects/${id}/documents`)}>
                    + {documents.length - 8} more documents — view all →
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right — supplier breakdown + actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Supplier breakdown */}
            <div style={s.card}>
              <div style={s.cardHeader}>
                <div style={s.cardTitle}>Supplier breakdown</div>
                <span style={s.cardLink} onClick={() => navigate('/suppliers')}>Registry →</span>
              </div>
              {suppliers.length === 0 ? (
                <div style={{ fontSize: 13, color: V.ink4, fontFamily: V.sans }}>No suppliers yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {suppliers.map(sup => {
                    const supDocs   = documents.filter(d => d.supplier === sup);
                    const isAwarded = supDocs.some(d => d.awarded);
                    const bestDoc   = supDocs.filter(d => d.total_value).sort((a, b) => a.total_value - b.total_value)[0];
                    const pct       = Math.round((supDocs.length / documents.length) * 100);
                    return (
                      <div key={sup}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: V.ink, fontFamily: V.sans }}>{sup}</div>
                            <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>
                              {supDocs.length} doc{supDocs.length !== 1 ? 's' : ''} · {isAwarded ? <span style={{ color: V.good, fontWeight: 600 }}>Awarded</span> : 'Not awarded'}
                            </div>
                          </div>
                          {bestDoc && (
                            <div style={{ fontFamily: V.mono, fontSize: 12, color: isAwarded ? V.gold : V.ink3, fontWeight: 500 }}>
                              {formatValue(bestDoc.total_value, bestDoc.currency)}
                            </div>
                          )}
                        </div>
                        <div style={{ height: 2, background: V.paper3, borderRadius: 1, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: isAwarded ? V.gold : V.ink3, borderRadius: 1, opacity: isAwarded ? 1 : 0.4, transition: 'width 0.4s ease' }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div style={s.card}>
              <div style={s.cardHeader}>
                <div style={s.cardTitle}>Quick actions</div>
              </div>
              <div style={s.qaGrid}>
                {[
                  { label: 'Compare documents', sub: 'Side-by-side analysis',  icon: '⧉', action: () => navigate(`/projects/${id}/documents`) },
                  { label: 'Consigliere',        sub: 'Ask the AI advisor',     icon: '◎', action: () => navigate(`/projects/${id}/chat`) },
                  { label: 'Timeline',           sub: 'Contract log',           icon: '↔', action: () => navigate(`/projects/${id}/timeline`) },
                  { label: 'Reports',            sub: 'Export Word or Excel',   icon: '↓', action: () => navigate(`/projects/${id}/documents`) },
                ].map((qa, i) => (
                  <div
                    key={i}
                    style={s.qaBtn}
                    onClick={qa.action}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = V.gold; e.currentTarget.style.background = '#fdf8ee'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = V.line; e.currentTarget.style.background = V.paper; }}
                  >
                    <div style={{ fontSize: 18, color: V.ink3 }}>{qa.icon}</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: V.ink, fontFamily: V.sans }}>{qa.label}</div>
                      <div style={{ fontSize: 10, color: V.ink4, fontFamily: V.sans, marginTop: 1 }}>{qa.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upload nudge if few docs */}
            {documents.length < 3 && (
              <div style={{ ...s.card, borderLeft: `2px solid ${V.gold}`, background: '#fdf8ee' }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: V.ink2, fontFamily: V.sans, marginBottom: 6 }}>
                  {documents.length === 0 ? 'Start by uploading your first document' : `Add ${3 - documents.length} more document${3 - documents.length !== 1 ? 's' : ''} to unlock full AI comparison`}
                </div>
                <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans, marginBottom: 12, lineHeight: 1.5 }}>
                  Upload at least 2 quotations from different suppliers to enable The Negotiation Room.
                </div>
                <button style={s.btnGold} onClick={() => setUploadOpen(true)}>Upload document</button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Modals */}
      {uploadOpen && (
        <UploadModal
          projectId={id}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => { setUploadOpen(false); load(); }}
        />
      )}
      {previewDoc && (
        <PreviewPanel
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────

const s = {
  wrap:         { minHeight: '100vh', background: V.paper2, fontFamily: V.sans, color: V.ink },

  utilBar:      { background: V.ink, height: 32, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12 },
  utilLeft:     { display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16, borderRight: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 },
  utilDot:      { width: 6, height: 6, borderRadius: '50%', background: V.gold },
  tickerLabel:  { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: V.gold, flexShrink: 0, textTransform: 'uppercase' },

  nav:          { background: V.paper, borderBottom: `1px solid ${V.line}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 0 },
  wordmark:     { display: 'flex', alignItems: 'baseline', fontFamily: V.serif, fontSize: 26, fontWeight: 600, letterSpacing: -0.5, lineHeight: 1, cursor: 'pointer' },
  wordmarkV:    { color: V.gold },
  wordmarkRest: { color: V.ink },
  wordmarkDivider: { width: 1, height: 18, background: V.line, margin: '0 12px' },
  wordmarkTag:  { fontSize: 10, fontWeight: 500, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink3, marginRight: 32, whiteSpace: 'nowrap' },
  navItems:     { display: 'flex', gap: 0, height: 60, alignItems: 'center' },
  navItem:      { padding: '0 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', height: 60, display: 'flex', alignItems: 'center', transition: 'color 0.15s', letterSpacing: 0.1 },
  navRight:     { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 },
  navUser:      { fontSize: 12, color: V.ink4 },
  navSignOut:   { background: 'none', border: 'none', fontSize: 12, color: V.ink3, cursor: 'pointer', padding: 0, fontFamily: V.sans },

  subTabBar:    { background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '0 32px', display: 'flex', alignItems: 'center', gap: 8, height: 44 },
  subTabView:   { fontSize: 9, fontWeight: 600, letterSpacing: 2, color: V.ink4, marginRight: 8, textTransform: 'uppercase' },
  subTab:       { fontFamily: V.sans, fontSize: 12, fontWeight: 500, padding: '5px 14px', borderRadius: 4, cursor: 'pointer', transition: 'all 0.15s', letterSpacing: 0.1 },

  breadBar:     { background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '0 48px', height: 52, display: 'flex', alignItems: 'center', gap: 10 },
  breadLink:    { fontSize: 13, color: V.ink4, cursor: 'pointer', fontFamily: V.sans },
  breadSep:     { fontSize: 13, color: V.line },
  breadCurrent: { fontSize: 13, color: V.ink, fontWeight: 500, fontFamily: V.sans },

  body:         { padding: '36px 48px 80px', maxWidth: 1440, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 28 },

  pageHead:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 24, borderBottom: `1px solid ${V.line}` },
  pageEyebrow:  { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: V.ink3, marginBottom: 10, fontFamily: V.sans },
  indBadge:     { fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 1, padding: '3px 8px', borderRadius: 3, textTransform: 'uppercase' },
  eyebrowSep:   { color: V.line },
  pageTitle:    { fontFamily: V.serif, fontSize: 32, fontWeight: 400, color: V.ink, lineHeight: 1.2, letterSpacing: -0.5, marginBottom: 6 },
  pageNotes:    { fontSize: 13, color: V.ink4, fontFamily: V.sans },
  statusBadge:  { fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 4, fontFamily: V.sans, letterSpacing: 0.3 },

  kpiStrip:     { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: V.line, border: `1px solid ${V.line}`, borderRadius: 4, overflow: 'hidden' },
  kpi:          { background: V.paper, padding: '20px 24px' },
  kpiLabel:     { fontSize: 9, fontWeight: 600, letterSpacing: 2, color: V.ink4, marginBottom: 10, textTransform: 'uppercase' },
  kpiVal:       { fontFamily: V.mono, fontSize: 32, fontWeight: 500, lineHeight: 1, letterSpacing: -1 },
  kpiSub:       { fontSize: 11, color: V.ink4, marginTop: 6 },

  twoCol:       { display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 18 },

  card:         { background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 6, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16 },
  cardHeader:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardTitle:    { fontFamily: V.serif, fontSize: 15, fontWeight: 500, color: V.ink },
  cardLink:     { fontSize: 12, color: V.ink3, cursor: 'pointer', fontFamily: V.sans },

  emptyState:   { padding: '8px 0' },

  docList:      { display: 'flex', flexDirection: 'column', gap: 0 },
  docRow:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 8px', borderRadius: 4, cursor: 'pointer', transition: 'background 0.12s', gap: 12 },
  docInfo:      { flex: 1, minWidth: 0 },
  docName:      { fontSize: 12, fontWeight: 500, color: V.ink, fontFamily: V.mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  docMeta:      { fontSize: 11, color: V.ink4, marginTop: 2, fontFamily: V.sans },
  docRight:     { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  docVal:       { fontFamily: V.mono, fontSize: 12, color: V.ink2, fontWeight: 500 },
  previewBtn:   { background: 'none', border: `0.5px solid ${V.line}`, borderRadius: 3, padding: '2px 7px', fontSize: 12, color: V.ink4, cursor: 'pointer', fontFamily: V.mono },

  qaGrid:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  qaBtn:        { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: `0.5px solid ${V.line}`, borderRadius: 5, cursor: 'pointer', background: V.paper, transition: 'all 0.15s' },

  overlay:      { position: 'fixed', inset: 0, background: 'rgba(12,24,48,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal:        { background: V.paper, borderRadius: 6, width: '100%', maxWidth: 580, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 64px rgba(12,24,48,0.22)' },
  modalHeader:  { padding: '22px 26px 18px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  modalTitle:   { fontFamily: V.serif, fontSize: 20, fontWeight: 500, color: V.ink },
  modalSub:     { fontSize: 12, color: V.ink4, marginTop: 3, fontFamily: V.sans },
  closeBtn:     { background: 'none', border: 'none', color: V.ink4, fontSize: 16, cursor: 'pointer', padding: 4, fontFamily: V.sans, textDecoration: 'none' },
  modalBody:    { padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 16, background: V.paper2, maxHeight: '75vh', overflowY: 'auto' },
  modalFooter:  { padding: '16px 26px', borderTop: `1px solid ${V.line}`, display: 'flex', justifyContent: 'flex-end', gap: 10, background: V.paper },

  field:        { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel:   { fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink3, fontFamily: V.sans },
  input:        { background: V.paper, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 12px', fontSize: 13, color: V.ink, fontFamily: V.sans, outline: 'none', width: '100%', boxSizing: 'border-box' },
  dropZone:     { border: `1.5px dashed ${V.line}`, borderRadius: 4, padding: '18px 16px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' },
  errorBox:     { background: V.badSoft, border: `1px solid ${V.bad}`, borderRadius: 4, padding: '10px 14px', fontSize: 12, color: V.bad },

  previewOverlay: { position: 'fixed', inset: 0, background: 'rgba(12,24,48,0.6)', zIndex: 200, display: 'flex', justifyContent: 'flex-end' },
  previewPanel:   { background: V.paper, width: '50%', minWidth: 480, maxWidth: 780, height: '100vh', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(12,24,48,0.15)' },
  previewHeader:  { padding: '20px 24px', borderBottom: `1px solid ${V.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: V.paper2, flexShrink: 0 },
  previewBody:    { flex: 1, padding: 16, overflow: 'hidden', minHeight: 0 },

  btnPrimary:   { background: V.ink, color: V.paper, border: 'none', borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: V.sans, whiteSpace: 'nowrap' },
  btnGold:      { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans, letterSpacing: 0.1, textDecoration: 'none', display: 'inline-block' },
  btnGhost:     { background: 'transparent', color: V.ink, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 400, cursor: 'pointer', fontFamily: V.sans, whiteSpace: 'nowrap', textDecoration: 'none' },
};
